/**
 * Every screen of the app, and which ones load on demand.
 *
 * Only the shell and Inicio are in the entry bundle, because together they
 * render the first screen. Each of the other screens is a separate chunk,
 * fetched the first time it is opened. The service worker precaches every
 * chunk anyway (vite.config.ts), so after the first visit opening a screen
 * offline costs a cache read, not a network request.
 *
 * The editor (Tiptap + ProseMirror, ~650 KiB), KaTeX and highlight.js used to
 * be downloaded and parsed on every start. Now they arrive only with the
 * screens that need them.
 *
 * See docs/modulos/pwa-y-despliegue.md#code-splitting.
 */
import type { ComponentType } from 'react'
import { DashboardPage } from '../pages/DashboardPage.tsx'
import { lazyPage } from './lazy-page.ts'

export { DashboardPage }

export const OnboardingPage = lazyPage(() => import('../pages/OnboardingPage.tsx'), 'OnboardingPage')
export const AuthCallbackPage = lazyPage(() => import('../pages/AuthCallbackPage.tsx'), 'AuthCallbackPage')
export const SessionPage = lazyPage(() => import('../pages/SessionPage.tsx'), 'SessionPage')
export const SessionCompletePage = lazyPage(() => import('../pages/SessionCompletePage.tsx'), 'SessionCompletePage')
export const ReviewSessionPage = lazyPage(() => import('../pages/SessionPage.tsx'), 'ReviewSessionPage')
export const ReviewCompletePage = lazyPage(() => import('../pages/SessionCompletePage.tsx'), 'ReviewCompletePage')
export const DeckDetailPage = lazyPage(() => import('../pages/DeckDetailPage.tsx'), 'DeckDetailPage')
export const DeckEditorPage = lazyPage(() => import('../pages/DeckEditorPage.tsx'), 'DeckEditorPage')
export const DecksPage = lazyPage(() => import('../pages/DecksPage.tsx'), 'DecksPage')
export const TagsPage = lazyPage(() => import('../pages/TagsPage.tsx'), 'TagsPage')
export const TagDetailPage = lazyPage(() => import('../pages/TagDetailPage.tsx'), 'TagDetailPage')
export const FreeStudyPage = lazyPage(() => import('../pages/FreeStudyPage.tsx'), 'FreeStudyPage')
export const NotesPage = lazyPage(() => import('../pages/NotesPage.tsx'), 'NotesPage')
export const NoteEditorPage = lazyPage(() => import('../pages/NoteEditorPage.tsx'), 'NoteEditorPage')
export const ImportPage = lazyPage(() => import('../pages/ImportPage.tsx'), 'ImportPage')
export const SettingsPage = lazyPage(() => import('../pages/SettingsPage.tsx'), 'SettingsPage')
export const AppearancePage = lazyPage(() => import('../pages/AppearancePage.tsx'), 'AppearancePage')
export const ExamHomePage = lazyPage(() => import('../pages/ExamHomePage.tsx'), 'ExamHomePage')
export const ExamBankPage = lazyPage(() => import('../pages/ExamBankPage.tsx'), 'ExamBankPage')
export const ExamSessionPage = lazyPage(() => import('../pages/ExamSessionPage.tsx'), 'ExamSessionPage')

/** A screen inside AppShell. `path` is relative to wherever the list is mounted. */
export type ShellRoute = { index: true; Page: ComponentType } | { path: string; Page: ComponentType }

/**
 * The screens inside AppShell. The real app mounts them at `/` and the Design
 * Lab (development only) at `/design-lab/all`, so the two can't drift apart.
 */
export const SHELL_ROUTES: readonly ShellRoute[] = [
  { index: true, Page: DashboardPage },
  { path: 'decks', Page: DecksPage },
  { path: 'deck/:id', Page: DeckDetailPage },
  { path: 'deck/:id/edit', Page: DeckEditorPage },
  { path: 'deck/:id/session/complete', Page: SessionCompletePage },
  { path: 'review/complete', Page: ReviewCompletePage },
  { path: 'tags', Page: TagsPage },
  { path: 'tags/:id', Page: TagDetailPage },
  { path: 'free-study', Page: FreeStudyPage },
  { path: 'notes', Page: NotesPage },
  { path: 'notes/:id', Page: NoteEditorPage },
  { path: 'import', Page: ImportPage },
  { path: 'settings', Page: SettingsPage },
  { path: 'appearance', Page: AppearancePage },
  { path: 'socratic', Page: ExamHomePage },
  { path: 'socratic/bank/:noteId', Page: ExamBankPage },
  { path: 'socratic/session', Page: ExamSessionPage },
]
